import { useSignTypedData as useSignPrivyTypedData } from '@privy-io/react-auth';
import { writeContract } from '@wagmi/core';
import { ethers } from 'ethers';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ContractFunctionExecutionError } from 'viem';
import { useSignTypedData } from 'wagmi';
import { z } from 'zod';

import config from '@/config';
import { getTransfers } from '@/features/account/api/get-transfers';
import { usePrivyAccount } from '@/hooks';
import { getEvmBalanceQueryKey } from '@/hooks/use-evm-balance';
import USDC_ABI from '@/lib/abis/mock-usdc-abi';
import { queryClient } from '@/lib/api-clients/query-client';
import { getChainDecimals, wagmiConfig } from '@/lib/wagmi/config';
import { useAccountStore } from '@/store/use-account-store';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';
import { FilledTransfer, useTransfersStore } from '@/store/use-transfers-store';
import { EventType } from '@/types';
import { ErrorCode, ErrorMsgs, OrderStatus } from '@/types/enums';
import { getBridgeAddress, getUSDCAddress } from '@/utils/addresses';
import { sendEvent } from '@/utils/analytics';
import {
  handleTransferForStore,
  transferResponseToTransfer,
} from '@/utils/handle-store-data';
import { bigIntToNum, formatBigInt } from '@/utils/value-format';

import { privateApi } from '../api-clients/rest-client';

const createDepositInputSchema = z.object({
  size: z.bigint(),
  chainId: z.number(),
});

type CreateDepositInput = z.infer<typeof createDepositInputSchema>;

type Options = {
  showToast?: boolean;
  waitForConfirm?: boolean;
};

export default function useCreateDeposit() {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { isEmbedded } = usePrivyAccount();
  const { signTypedData: signPrivyTypedData } = useSignPrivyTypedData();
  const { signTypedDataAsync: signTypedData } = useSignTypedData();

  const { email } = usePrivyAccount();

  return {
    isLoading,
    createDeposit: async (
      { size, chainId }: CreateDepositInput,
      options: Options = { showToast: true },
    ) => {
      setIsLoading(true);

      let promise: Promise<any>;

      const {
        address = '',
        getAccurateTime,
        nextNonce,
      } = useAccountStore.getState();
      const time = getAccurateTime();

      if (isEmbedded || config.isDevelopment) {
        const validAfter = 0;
        const validBefore = Math.round(time / 1000) + 3600;

        const nonce = ethers.zeroPadValue(ethers.toBeHex(nextNonce()), 32);

        let signature: string;

        const message = {
          domain: {
            name: import.meta.env.VITE_AUTHORIZED_TRANSFER_DOMAIN_NAME,
            version: import.meta.env.VITE_AUTHORIZED_TRANSFER_DOMAIN_VERSION,
            chainId,
            verifyingContract: getUSDCAddress(chainId),
          },
          account: address as `0x${string}`,
          primaryType: 'TransferWithAuthorization' as const,
          types: {
            TransferWithAuthorization: [
              { name: 'from', type: 'address' },
              { name: 'to', type: 'address' },
              { name: 'value', type: 'uint256' },
              { name: 'validAfter', type: 'uint256' },
              { name: 'validBefore', type: 'uint256' },
              { name: 'nonce', type: 'bytes32' },
            ],
          },
          message: {
            from: address,
            to: getBridgeAddress(chainId),
            value: Number(size),
            validAfter,
            validBefore,
            nonce,
          },
        };

        if (isEmbedded) {
          signature = (
            await signPrivyTypedData(message, {
              uiOptions: { showWalletUIs: false },
            })
          ).signature;
        } else {
          signature = await signTypedData(message);
        }

        promise = privateApi.post('/transfer/deposit/authorized', {
          account: address,
          value: Number(size),
          valid_after: validAfter,
          valid_before: validBefore,
          nonce,
          signature,
          chain_id: chainId,
        });
      } else {
        // signature = await signTypedData(wagmiConfig, message);
        promise = writeContract(wagmiConfig, {
          abi: USDC_ABI,
          address: getUSDCAddress(chainId),
          functionName: 'transfer',
          args: [getBridgeAddress(chainId), size],
        });
      }

      const confirmPromise = promise.then((_data) => {
        sendEvent(EventType.DEPOSIT_COMPLETED, {
          amount: bigIntToNum(size, COLLATERAL_DECIMALS),
          email,
        });
        return new Promise<FilledTransfer>((resolve, reject) => {
          // eslint-disable-next-line prefer-const
          let subscriberTimeoutId: NodeJS.Timeout;
          // eslint-disable-next-line prefer-const
          let pollIntervalId: NodeJS.Timeout;
          // eslint-disable-next-line prefer-const
          let unsubscribe: () => void;

          const cleanup = () => {
            clearTimeout(subscriberTimeoutId);
            clearInterval(pollIntervalId);
            unsubscribe();
          };

          unsubscribe = useTransfersStore.subscribe((state) => {
            const newDeposits = state.newDeposits[address] ?? [];

            if (newDeposits.length > 0) {
              for (const deposit of newDeposits) {
                if (
                  deposit.postTime >= time &&
                  ('size' in deposit
                    ? formatBigInt(deposit.size, COLLATERAL_DECIMALS) ===
                      formatBigInt(size, getChainDecimals(chainId))
                    : true)
                ) {
                  state.removeNewDeposit(deposit.id, address);
                  if (deposit.status === OrderStatus.FILLED) {
                    cleanup();
                    resolve(deposit);
                  } else if (deposit.status === OrderStatus.REJECTED) {
                    cleanup();
                    reject(deposit.code as ErrorCode);
                  }
                }
              }
            }
          });

          subscriberTimeoutId = setTimeout(() => {
            cleanup();
            reject(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
          }, 30_000); // 30 seconds timeout

          pollIntervalId = setInterval(() => {
            getTransfers({ startTime: time }).then((res) => {
              res.forEach((data) => {
                const transfer = transferResponseToTransfer(data);
                handleTransferForStore(transfer);
              });
              return res;
            });
          }, 2_000);
        });
      });

      confirmPromise.then(() => {
        queryClient.refetchQueries({
          queryKey: getEvmBalanceQueryKey(chainId, address),
        });
      });

      confirmPromise.finally(() => setIsLoading(false));

      if (options?.showToast) {
        toast.promise(confirmPromise, {
          loading: config.isDevelopment
            ? t('createDeposit.funding')
            : t('createDeposit.depositing'),
          success: (data: FilledTransfer) => {
            return `${config.isDevelopment ? t('createDeposit.funded') : t('createDeposit.deposited')} ${formatBigInt(data.size, COLLATERAL_DECIMALS, { digits: 2 })} USD`;
          },
          error: (error) => {
            const errorMessage =
              ErrorMsgs[error as ErrorCode] ||
              error?.toString() ||
              t('createDeposit.unhandledError');
            return `${config.isDevelopment ? t('createDeposit.fundingFailed') : t('createDeposit.depositFailed')} ${errorMessage}`;
          },
        });
      }

      // Catch initial txn errors
      promise.catch((error) => {
        if (error instanceof ContractFunctionExecutionError) {
          toast.error(error.cause.shortMessage);
        } else {
          console.warn('Unhandled error');
          console.error(error);
        }
      });

      return options?.waitForConfirm ? confirmPromise : promise;
    },
  };
}
